/**
 * Public brand-themed course-enquiry embed form.
 *
 * One reusable component, three routes:
 *   /embed/probound-enquiry   <EmbedCourseEnquiry brand="probound" />
 *   /embed/proepoxy-enquiry   <EmbedCourseEnquiry brand="proepoxy" />
 *   /embed/prodecor-enquiry   <EmbedCourseEnquiry brand="prodecor" />
 *
 * No auth, no chrome — designed to be iframed onto the brand-specific
 * course pages of resintrainingcourses.co.uk. Submissions email the
 * training inbox; an existing Zapier flow forwards to Google Sheets +
 * Superchat WhatsApp.
 */
import React, { useState, useEffect } from 'react';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { HearAboutRadios } from '@/components/embed/HearAboutRadios';
import { useEmbedFormConfig } from '@/hooks/useEmbedFormConfig';
import { useGoogleAdsTag, fireEnquiryConversion } from '@/utils/googleAds';

// Visual theme per brand — kept inline so the embed has zero external CSS
// dependencies and any brand colour drift only happens in this file.
const BRAND_THEME = {
  probound: {
    label: 'ProBound',
    eyebrow: 'PROBOUND · COURSE ENQUIRY',
    headline: 'Enquire about a ProBound training course',
    headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)',
    accent: '#D4A24B',
    buttonBg: '#D4A24B',
    buttonInk: '#0B0B0E',
    labelInk: '#1f4030',
  },
  proepoxy: {
    label: 'ProEpoxy',
    eyebrow: 'PROEPOXY · COURSE ENQUIRY',
    headline: 'Enquire about a ProEpoxy training course',
    headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#5b21b6 80%)',
    accent: '#a78bfa',
    buttonBg: '#5b21b6',
    buttonInk: '#fff',
    labelInk: '#3b0764',
  },
  prodecor: {
    label: 'ProDecor',
    eyebrow: 'PRODECOR · COURSE ENQUIRY',
    headline: 'Enquire about a ProDecor training course',
    headerBg: 'linear-gradient(135deg,#0B0B0E 0%,#9a3412 80%)',
    accent: '#fb923c',
    buttonBg: '#c2410c',
    buttonInk: '#fff',
    labelInk: '#7c2d12',
  },
};

const initialForm = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  message: '',
  hear_about: '',
  hear_about_other: '',
  interested_courses: [],
  company_website: '', // honeypot
};

export default function EmbedCourseEnquiry({ brand }) {
  const theme = BRAND_THEME[brand] || BRAND_THEME.probound;
  useGoogleAdsTag();
  const copy = useEmbedFormConfig(brand, {
    eyebrow: theme.eyebrow, headline: theme.headline,
    intro: 'Complete the form below to receive our course information pack and one of the team will be in touch.',
    submit_label: `Send ${theme.label} enquiry`, success_title: 'Thanks \u2014 we\u2019ve got it.',
    success_body: 'You\u2019ll hear from us shortly on WhatsApp or email.',
    footer_note: 'We only use these details to reply to your course enquiry.',
    message_label: 'Message (optional)', message_placeholder: 'Which course / dates are you after? Anything we should know?',
    courses_label: 'Which courses are you most interested in? (tick all that apply)', hear_about_label: 'Where did you hear about us?',
    show_message: true, show_courses: true, show_hear_about: true, hear_about_required: false,
  });
  useEffect(() => { setForm((f) => (f.interested_courses.length ? f : { ...f, interested_courses: [brand] })); }, [brand]);
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentRef, setSentRef] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    // Match backend validation exactly so users get the same feedback locally
    // as they would on a 422.
    if (!form.first_name.trim() || !form.last_name.trim()) return setError('Please enter your name.');
    if (!form.phone.trim()) return setError('Please enter a phone number.');
    if (!form.email.trim()) return setError('Please enter an email address.');
    if (copy.show_hear_about && copy.hear_about_required && !form.hear_about) return setError('Please tell us how you heard about us.');
    if (form.hear_about === 'other' && !form.hear_about_other.trim()) return setError('Please add a quick note about where you heard about us.');
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/public/course-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, brand }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Submission failed — please try again.');
      if (data.ref) fireEnquiryConversion(data.ref);
      setSentRef(data.ref || 'received');
      setForm(initialForm);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f6f5f0',
        padding: '24px 16px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
      data-testid={`embed-course-${brand}-root`}
    >
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <header
          style={{
            background: theme.headerBg,
            color: '#fff',
            padding: '22px 24px',
            borderRadius: '14px 14px 0 0',
            borderBottom: `3px solid ${theme.accent}`,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.75 }}>{copy.eyebrow}</div>
          <h1 style={{ margin: '6px 0 2px', fontSize: 22, fontWeight: 800 }}>{copy.headline}</h1>
          {copy.intro && <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>{copy.intro}</p>}
        </header>

        {sentRef ? (
          <div
            data-testid="embed-course-success"
            style={{
              background: '#fff',
              padding: '32px 22px',
              borderRadius: '0 0 14px 14px',
              border: '1px solid #eadfca',
              borderTop: 0,
              textAlign: 'center',
            }}
          >
            <div style={{ width: 56, height: 56, margin: '0 auto 12px', background: theme.accent, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0B0B0E', fontSize: 28, fontWeight: 800 }}>✓</div>
            <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#0B0B0E' }}>{copy.success_title}</h2>
            <p style={{ margin: '0 0 12px', color: '#555', fontSize: 14 }}>{copy.success_body}</p>
            <p style={{ margin: 0, color: '#888', fontSize: 12 }}>Reference: <strong>{sentRef}</strong></p>
            <button
              type="button"
              onClick={() => setSentRef('')}
              data-testid="embed-course-new"
              style={{
                marginTop: 20, background: 'transparent', color: theme.labelInk,
                border: `1px solid ${theme.labelInk}`, padding: '8px 16px', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Send another enquiry
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            style={{
              background: '#fff',
              padding: '22px',
              borderRadius: '0 0 14px 14px',
              border: '1px solid #eadfca',
              borderTop: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
            data-testid={`embed-course-${brand}-form`}
          >
            <Row>
              <Field theme={theme} label="First name *" value={form.first_name} onChange={set('first_name')} testid="embed-course-first" autoComplete="given-name" />
              <Field theme={theme} label="Last name *"  value={form.last_name}  onChange={set('last_name')}  testid="embed-course-last"  autoComplete="family-name" />
            </Row>
            <Row>
              <Field theme={theme} label="Phone number *" value={form.phone} onChange={set('phone')} testid="embed-course-phone" type="tel" autoComplete="tel" />
              <Field theme={theme} label="Email address *" value={form.email} onChange={set('email')} testid="embed-course-email" type="email" autoComplete="email" />
            </Row>

            {copy.show_courses && (
              <div>
                <Label theme={theme}>{copy.courses_label}</Label>
                <CoursePicker value={form.interested_courses} onChange={(v) => setForm((f) => ({ ...f, interested_courses: v }))} accent={theme.accent} />
              </div>
            )}

            {copy.show_message && (
              <div>
                <Label theme={theme}>{copy.message_label}</Label>
                <textarea
                  value={form.message}
                  onChange={set('message')}
                  data-testid="embed-course-message"
                  rows={4}
                  placeholder={copy.message_placeholder}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
                />
              </div>
            )}

            {copy.show_hear_about && (
              <div>
                <Label theme={theme}>{copy.hear_about_label}{copy.hear_about_required ? ' *' : ''}</Label>
                <HearAboutRadios
                  value={form.hear_about}
                  onChange={(v) => setForm((f) => ({ ...f, hear_about: v }))}
                  accent={theme.labelInk}
                  testid="embed-course-hear-about"
                />
              </div>
            )}
            {copy.show_hear_about && form.hear_about === 'other' && (
              <Field
                theme={theme}
                label="Tell us where *"
                value={form.hear_about_other}
                onChange={set('hear_about_other')}
                testid="embed-course-hear-other"
                placeholder="e.g. a podcast / Google search for…"
              />
            )}

            {/* Honeypot — CSS-hidden, bots auto-fill it, humans never see. */}
            <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
              <label>
                Company website (leave blank)
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company_website}
                  onChange={set('company_website')}
                  data-testid="embed-course-honeypot"
                />
              </label>
            </div>

            {error && (
              <div data-testid="embed-course-error" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="embed-course-submit"
              style={{
                marginTop: 4,
                background: theme.buttonBg,
                color: theme.buttonInk,
                border: 0,
                padding: '13px 16px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: 0.3,
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.6 : 1,
                transition: 'transform .12s ease, opacity .12s ease',
              }}
            >
              {busy ? 'Sending…' : copy.submit_label}
            </button>
            {copy.footer_note && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#888', textAlign: 'center' }}>{copy.footer_note}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Local presentational helpers ──

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  border: '1px solid #d8d0bd',
  borderRadius: 8,
  background: '#fff',
  fontSize: 14,
  fontFamily: 'inherit',
  color: '#0B0B0E',
  outline: 'none',
};


/** Tick the courses the lead is interested in — shown on the staff row and in the alert email. */
const COURSE_OPTIONS = [
  { key: 'probound', label: 'Resin Bound Stone, Rubber and Coloured Quartz Surfacing', sub: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'Metallic Epoxy Flooring and Worktops & Commercial Epoxy Flooring', sub: 'ProEpoxy', color: '#6B2FA0' },
  { key: 'prodecor', label: 'Decorative Walling such as Venetian Plaster and Concrete Effects', sub: 'ProDecor', color: '#B8860B' },
];
function CoursePicker({ value, onChange, accent = '#0B0B0E' }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div data-testid="embed-courses">
      <div style={{ display: 'grid', gap: 8 }}>
        {COURSE_OPTIONS.map((c) => {
          const on = value.includes(c.key);
          return (
            <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${on ? c.color : '#e5e7eb'}`, background: on ? `${c.color}14` : '#fff', transition: 'border-color .15s, background .15s' }}
              data-testid={`embed-course-${c.key}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(c.key)} style={{ width: 18, height: 18, accentColor: c.color, margin: 0 }} data-testid={`embed-course-tick-${c.key}`} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: '#111827' }}>{c.label}</span>
                <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: c.color }}>{c.sub}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Label({ children, theme }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: theme.labelInk, marginBottom: 6, letterSpacing: 0.2 }}>
      {children}
    </div>
  );
}

function Field({ theme, label, value, onChange, testid, type = 'text', autoComplete, placeholder }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <Label theme={theme}>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testid}
        style={inputStyle}
        autoComplete={autoComplete}
        placeholder={placeholder}
      />
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div>;
}
